import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'
import { AuthProvider } from '@/hooks/useAuth'
import { TwilioProvider } from './contexts/TwilioProviderLazy'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { initSentry, captureException } from '@/lib/sentry'
import { SentryErrorFallback } from '@/components/common/SentryErrorFallback'
import { ImpersonationProvider } from '@/contexts/ImpersonationContext'

const Analytics = lazy(() =>
  import('@vercel/analytics/react').then((m) => ({ default: m.Analytics })),
)
const SpeedInsights = lazy(() =>
  import('@vercel/speed-insights/react').then((m) => ({ default: m.SpeedInsights })),
)

const CHUNK_RELOAD_GUARD_KEY = 'divus-chunk-reload-attempted';

function isDynamicImportFailure(reason: unknown): boolean {
  const message = String((reason as any)?.message || reason || '').toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('loading chunk')
  );
}

function reloadOnceForChunkError() {
  const alreadyTried = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1';
  if (alreadyTried) return;
  sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
  window.location.reload();
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadOnceForChunkError();
});

window.addEventListener('unhandledrejection', (event) => {
  if (!isDynamicImportFailure(event.reason)) return;
  event.preventDefault();
  reloadOnceForChunkError();
});

// Resilient ErrorBoundary that filters non-fatal removeChild errors
// instead of showing the fatal "Algo deu errado" screen
function isRemoveChildError(error: unknown): boolean {
  if (error instanceof Error && error.message?.includes('removeChild')) return true;
  if (error instanceof DOMException && error.message?.includes('removeChild')) return true;
  // Safari variant: same DOM error, different message
  if (error instanceof DOMException && error.name === 'NotFoundError') return true;
  if (error instanceof Error && error.message?.includes('The object can not be found here')) return true;
  return false;
}

class ResilientErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    if (isRemoveChildError(error)) {
      // Don't activate fallback for removeChild errors
      return null;
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isRemoveChildError(error)) {
      console.warn('[ResilientErrorBoundary] Suppressed non-fatal removeChild error:', error.message);
      return;
    }
    // Report real errors to Sentry
    captureException(error, { componentStack: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <SentryErrorFallback
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}

// Monkey-patch removeChild to prevent Radix UI Portal crashes
// This is a known React/Radix issue where Portal unmounting during navigation
// throws a non-fatal DOMException that crashes the ErrorBoundary
const originalRemoveChild = Node.prototype.removeChild;
// @ts-ignore - intentional monkey-patch
Node.prototype.removeChild = function <T extends Node>(child: T): T {
  if (child.parentNode !== this) {
    if (import.meta.env.DEV) {
      console.warn('[DOM] removeChild: node not a child, ignoring silently.');
    }
    return child;
  }
  return originalRemoveChild.call(this, child) as T;
};

// Initialize Sentry off the critical path so /auth first paint isn't blocked
const scheduleIdle: (cb: () => void) => void =
  typeof (window as any).requestIdleCallback === 'function'
    ? (cb) => (window as any).requestIdleCallback(cb, { timeout: 2000 })
    : (cb) => setTimeout(cb, 0)

scheduleIdle(() => {
  initSentry()
  if (import.meta.env.DEV) {
    console.log(
      '✅ Sentry initialized with DSN:',
      import.meta.env.VITE_SENTRY_DSN ? 'configured' : 'MISSING!'
    )
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 15, // 15 minutes - reduce re-fetches
      gcTime: 1000 * 60 * 30, // 30 minutes cache
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ResilientErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ImpersonationProvider>
              <TwilioProvider>
                <App />
                <Suspense fallback={null}>
                  <Analytics />
                  <SpeedInsights />
                </Suspense>
              </TwilioProvider>
            </ImpersonationProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ResilientErrorBoundary>
  </React.StrictMode>,
)
